/* eslint-disable no-comments/disallowComments */
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { MainEntryAemLabels, MainEntryState } from './definitions';
import { LoggerService } from '../../services/logger/logger.service';
import { AemApiService } from '../../services/api/api.aem.service';
import { ApiResponse } from '../../definitions/api-response.interface';
import { MainEntryFactory } from './main-entry.factory';
import { expEnvironmentRef } from '../../injection-tokens/environment.injection-token';
import { EnvironmentRef } from 'projects/tmhna-portal/src/environments/interfaces';
import { Meta, Title } from '@angular/platform-browser';


@Injectable({
	providedIn: 'root'
})
export class MainEntryService
{
	private readonly state$: BehaviorSubject<MainEntryState> =
		new BehaviorSubject<MainEntryState>(MainEntryFactory.build());
	constructor(
	public aemApiService: AemApiService,
	public loggerService: LoggerService,
	private readonly metaService: Meta,
	private readonly titleService: Title,
	@Inject(expEnvironmentRef) readonly environmentRef: EnvironmentRef
	) {}

	aemBaseUrl: string = this.environmentRef.aemBaseUrl;
	isRaymond : boolean | undefined = this.environmentRef?.isRaymond;

	get state(): MainEntryState
	{
		return this.state$.value;
	}
	get stateChanged(): Observable<MainEntryState>
	{
		return this.state$.asObservable();
	}

	public updateState(value: MainEntryState): void
	{
		this.state$.next(value);
	}

	public getLandingPageAemLabels()
	{
		this.state.isLoading = true;
		this.aemApiService.getLandingPageLabels().subscribe(
			{
				next: (res: ApiResponse) =>
				{
					this.state.isLoading = false;
					const landingPageLabels = res.data as MainEntryAemLabels;
					this.setSectionTopLabels(landingPageLabels);
					this.setSectionMiddleLabels(landingPageLabels);
					this.isRaymond ? this.setSectionBottomLabelsForRaymond(landingPageLabels) : this.setSectionBottomLabels(landingPageLabels);
					this.updateState(this.state);
				},
				error: () =>
				{
					this.state.isLoading = false;
				}
			}
		);
	}

	public setSectionTopLabels(landingPageLabels: MainEntryAemLabels): void
	{
		this.state.sectionTop.globalHeader = landingPageLabels.globalHeader;
		this.state.sectionTop.tags = landingPageLabels.tags;

		this.state.sectionTop.globalHeader[0].iconPath = this.isRaymond ? landingPageLabels.footer.footercontent.logoImagePath : this.aemBaseUrl + this.state.sectionTop.globalHeader[0].iconPath;

		this.state.sectionTop.tags.forEach(el =>
		{
			if(el.type.toLowerCase() === 'title')
			{
				this.titleService.setTitle(el.description);
			}
			if(el.type.toLowerCase() === 'meta')
			{
				this.metaService.addTag({ name: 'description', content: el.description });
			}
		});
	}

	public setSectionMiddleLabels(landingPageLabels: MainEntryAemLabels): void
	{
		this.state.sectionMiddle.banner.bannerImageDesktopView = landingPageLabels.banner.bannerImageDesktopView;
		this.state.sectionMiddle.banner.bannerImageDesktopView.iconPath = ( this.isRaymond ? '' : this.aemBaseUrl ) + landingPageLabels.banner.bannerImageDesktopView.iconPath;
		this.state.sectionMiddle.banner.bannerImageMobileView = landingPageLabels.banner.bannerImageMobileView;
		this.state.sectionMiddle.banner.bannerImageMobileView.iconPath = this.aemBaseUrl + landingPageLabels.banner.bannerImageMobileView.iconPath;
		this.state.sectionMiddle.banner.bulletPoints = landingPageLabels.banner.bulletPoints;
		this.state.sectionMiddle.banner.header = landingPageLabels.banner.header;
		this.state.sectionMiddle.banner.registerButton.label = landingPageLabels.banner.registerButton;
		this.state.sectionMiddle.banner.subText = landingPageLabels.banner.subText;
		this.state.sectionMiddle.iconsDeskTopView = landingPageLabels.iconsDeskTopView;
		if(!this.isRaymond)
		{
			this.state.sectionMiddle.iconsMobileView = landingPageLabels.iconsMobileView;
			this.state.sectionMiddle.iconsDeskTopView[0].icon.iconPath = this.aemBaseUrl + this.state.sectionMiddle.iconsDeskTopView[0].icon.iconPath;
			this.state.sectionMiddle.iconsDeskTopView[1].icon.iconPath = this.aemBaseUrl + this.state.sectionMiddle.iconsDeskTopView[1].icon.iconPath;
			this.state.sectionMiddle.iconsDeskTopView[2].icon.iconPath = this.aemBaseUrl + this.state.sectionMiddle.iconsDeskTopView[2].icon.iconPath;
			this.state.sectionMiddle.iconsDeskTopView[3].icon.iconPath = this.aemBaseUrl + this.state.sectionMiddle.iconsDeskTopView[3].icon.iconPath;
			this.state.sectionMiddle.iconsDeskTopView[4].icon.iconPath = this.aemBaseUrl + this.state.sectionMiddle.iconsDeskTopView[4].icon.iconPath;
		}
	}

	public setSectionBottomLabelsForRaymond(landingPageLabels: MainEntryAemLabels): void
	{
		this.state.sectionBottom.footer.altText = landingPageLabels.footer.altText;
		this.state.sectionBottom.footer.cssClass = landingPageLabels.footer.cssClass;
		this.state.sectionBottom.footer.footercontent.altText = landingPageLabels.footer.footercontent.altText;
		this.state.sectionBottom.footer.footercontent.copyright = landingPageLabels.footer.footercontent.copyright;
		this.state.sectionBottom.footer.footercontent.fixed = landingPageLabels.footer.footercontent.fixed;
		this.state.sectionBottom.footer.footercontent.hidden = landingPageLabels.footer.footercontent.hidden;
		this.state.sectionBottom.footer.footercontent.logoImagePath = landingPageLabels.footer.footercontent.logoImagePath;
		this.state.sectionBottom.footer.footercontent.footermenu = landingPageLabels.footer.footercontent.footermenu;
		const currentUrl = window?.location.href;
		if(currentUrl.includes('contact-us') )
		{
			this.state.sectionBottom.footer.footercontent.footermenu = landingPageLabels.footer.footercontent.footermenu.filter((item) => item.label !== 'Support');
		}

	}

	public setSectionBottomLabels(landingPageLabels: MainEntryAemLabels): void
	{
		this.state.sectionBottom.footer.footercontent.unifiedFooter = landingPageLabels.footer.footercontent.unifiedFooter;
		this.state.sectionBottom.footer.footercontent.css.ecomm = landingPageLabels.footer.footercontent.css.ecomm;
		this.state.sectionBottom.footer.footercontent.script = landingPageLabels.footer.footercontent.script;
		this.aemApiService.loadFooterStyles(this.state.sectionBottom.footer.footercontent.css.ecomm);
		this.aemApiService.loadFooterScripts(this.state.sectionBottom.footer.footercontent.script);
	}
}
